"use client";
/**
 * ResponsiveDialog — drop-in replacement for Dialog that renders as a
 * bottom Drawer (sheet) on mobile and as a centered Dialog on desktop.
 *
 * Usage (identical to <Dialog>):
 *
 *   <ResponsiveDialog open={open} onOpenChange={setOpen}>
 *     <ResponsiveDialogContent className="max-w-2xl">
 *       <ResponsiveDialogHeader>
 *         <ResponsiveDialogTitle>Title</ResponsiveDialogTitle>
 *         <ResponsiveDialogDescription>...</ResponsiveDialogDescription>
 *       </ResponsiveDialogHeader>
 *       ...body...
 *     </ResponsiveDialogContent>
 *   </ResponsiveDialog>
 *
 * Triggers and Close children are remapped automatically.
 */
import React from "react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "./dialog";
import {
  Drawer,
  DrawerTrigger,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
  DrawerClose,
} from "./drawer";
import { cn } from "../../lib/utils";

// SSR-safe useMediaQuery
function useIsDesktop(query = "(min-width: 768px)") {
  const [isDesktop, setIsDesktop] = React.useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return window.matchMedia(query).matches;
  });
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const m = window.matchMedia(query);
    const onChange = (e) => setIsDesktop(e.matches);
    if (m.addEventListener) m.addEventListener("change", onChange);
    else m.addListener(onChange);
    return () => {
      if (m.removeEventListener) m.removeEventListener("change", onChange);
      else m.removeListener(onChange);
    };
  }, [query]);
  return isDesktop;
}

const DesktopCtx = React.createContext(true);

export function ResponsiveDialog({ children, ...props }) {
  const isDesktop = useIsDesktop();
  const Root = isDesktop ? Dialog : Drawer;
  return (
    <DesktopCtx.Provider value={isDesktop}>
      <Root {...props}>{children}</Root>
    </DesktopCtx.Provider>
  );
}

export function ResponsiveDialogTrigger(props) {
  const isDesktop = React.useContext(DesktopCtx);
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  return <Trigger {...props} />;
}

export const ResponsiveDialogContent = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    const isDesktop = React.useContext(DesktopCtx);
    if (isDesktop) {
      return (
        <DialogContent ref={ref} className={className} {...props}>
          {children}
        </DialogContent>
      );
    }
    return (
      <DrawerContent
        ref={ref}
        className={cn("max-h-[92vh] overflow-y-auto px-4 pb-6 safe-bottom", className)}
        {...props}
      >
        {children}
      </DrawerContent>
    );
  },
);
ResponsiveDialogContent.displayName = "ResponsiveDialogContent";

export function ResponsiveDialogHeader({ className, ...props }) {
  const isDesktop = React.useContext(DesktopCtx);
  const Header = isDesktop ? DialogHeader : DrawerHeader;
  return <Header className={className} {...props} />;
}

export function ResponsiveDialogFooter({ className, ...props }) {
  const isDesktop = React.useContext(DesktopCtx);
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  return <Footer className={className} {...props} />;
}

export const ResponsiveDialogTitle = React.forwardRef((props, ref) => {
  const isDesktop = React.useContext(DesktopCtx);
  const Title = isDesktop ? DialogTitle : DrawerTitle;
  return <Title ref={ref} {...props} />;
});
ResponsiveDialogTitle.displayName = "ResponsiveDialogTitle";

export const ResponsiveDialogDescription = React.forwardRef((props, ref) => {
  const isDesktop = React.useContext(DesktopCtx);
  const Description = isDesktop ? DialogDescription : DrawerDescription;
  return <Description ref={ref} {...props} />;
});
ResponsiveDialogDescription.displayName = "ResponsiveDialogDescription";

export function ResponsiveDialogClose(props) {
  const isDesktop = React.useContext(DesktopCtx);
  const Close = isDesktop ? DialogClose : DrawerClose;
  return <Close {...props} />;
}
